<template>
  <div class="p-the-header-navi-on-admin">
    <div class="p-the-header-navi-on-admin__item">
      <ul class="p-the-header-navi-on-admin__navi">
        <li
          v-for="({
            label,
            url,
            iconComponentName,
            childList,
            dividingLine,
            hasNotification
          },
          key) in naviList"
          :key="key"
          class="p-the-header-navi-on-admin__navi__item"
          :class="dividingLineClassName(dividingLine)"
        >
          <NaviListLinkOnAdmin
            :url="url"
            :current-element-key="key"
            :icon-component-name="iconComponentName"
            :child-list="childList"
            :thanks-point-count="thanksPointCount"
            :shown="shownChildItemState(key)"
            :has-notification="hasNotification"
            @on-click="onClickDropdown"
          >
            <template v-if="iconComponentName === 'IconPoint'" #icon>
              <IconPoint
                :icon-size="24"
                :icon-type="'default'"
                :icon-color="'#616161'"
                :border-color="hoverColor[isHover(key)]"
              />
            </template>

            <template v-else #icon>
              <IconBase
                :width="24"
                :height="24"
                view-box="0 0 24 24"
                icon-color="transparent"
              >
                <component
                  :is="iconComponentName"
                  :icon-color="hoverColor[isHover(key)]"
                  :border-color="hoverColor[isHover(key)]"
                />
              </IconBase>
            </template>

            <template #label>
              {{ label }}
            </template>
          </NaviListLinkOnAdmin>

          <TheHeaderNaviChildOnAdmin
            v-if="childList"
            :child-list="childList"
            :thanks-point-count="thanksPointCount"
            :shown="shownChildItemState(key)"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import NaviListLinkOnAdmin from "@/common/components/organisms/header/admin/NaviListLinkOnAdmin.vue";
import TheHeaderNaviChildOnAdmin from "@/common/components/organisms/header/admin/TheHeaderNaviChildOnAdmin.vue";
import IconBase from "@/common/components/atoms/IconBase.vue";

import IconBarGraph from "@/common/components/icons/IconBarGraph.vue";
import IconHuman from "@/common/components/icons/IconHuman.vue";
import IconGroupOfHumans from "@/common/components/icons/IconGroupOfHumans.vue";
import IconPoint from "@/common/components/icons/IconPoint.vue";
import IconNote from "@/common/components/icons/IconNote.vue";
import IconKey from "@/common/components/icons/IconKey.vue";
import IconUpload from "@/common/components/icons/IconUpload.vue";
import IconDownload from "@/common/components/icons/IconDownload.vue";
import IconTools from "@/common/components/icons/IconTools.vue";
import commons from "@/common/store/modules/commons";
import { getThanksPointName } from "@/common/assets/js/utils/pointName";
import CONST_DATA from "@/general/mixins/const/constData";

import permissions from "@/admin/store/modules/rolePermissions";

@Options({
  props: {
    thanksPointCount: { type: Number, default: 0 }
  },
  components: {
    NaviListLinkOnAdmin,
    TheHeaderNaviChildOnAdmin,
    IconBase,
    IconBarGraph,
    IconHuman,
    IconGroupOfHumans,
    IconPoint,
    IconNote,
    IconKey,
    IconUpload,
    IconDownload,
    IconTools
  }
})
export default class TheHeaderNaviOnAdmin extends Vue {
  thanksPointCount!: number;

  get colorCode() {
    return commons?.colorCode;
  }

  hoverColor: any = {
    true: this.colorCode,
    false: "#616161"
  };
  currentHoverElementKey = -1;

  currentOpenDropdown: string[] = [];

  naviList: any = [];

  isHover() {
    const { currentHoverElementKey } = this;
    return (currentKey: any) => currentHoverElementKey === currentKey;
  }

  shownChildItemState(key: any) {
    const { currentOpenDropdown } = this;
    return currentOpenDropdown.includes(key);
  }

  dividingLineClassName() {
    return (dividingLine: any) => {
      const resultClassName = dividingLine
        ? "p-the-header-navi-on-admin__navi__item--divid"
        : "";
      return resultClassName;
    };
  }

  mounted() {
    this.initCustomColor();
  }

  get hasDashboardPermissions() {
    const RP_DB_ALL = ["Dashboard", "Read", "Overall"];
    const RP_DB_GROUP = ["Dashboard", "Read", "Group"];
    const RP_DB_USER = ["Dashboard", "Read", "User"];

    return permissions.hasOnePermissions([RP_DB_ALL, RP_DB_GROUP, RP_DB_USER]);
  }

  get hasGrantPointListPermission() {
    const RP_GP_LIST = ["GrantPoint", "List"];
    return permissions.hasPermission(RP_GP_LIST);
  }

  get hasGrantPointCreatePermissions() {
    const RP_GP_CREATE_NO_LIMIT = ["GrantPoint", "Create", "NoLimit"];
    const RP_GP_CREATE_THANKS = ["GrantPoint", "Create", "Thanks"];
    const RP_GP_CREATE_GRANT = ["GrantPoint", "Create", "Grant"];

    return permissions.hasOnePermissions([
      RP_GP_CREATE_NO_LIMIT,
      RP_GP_CREATE_THANKS,
      RP_GP_CREATE_GRANT
    ]);
  }

  get hasGrantCriteriaListPermission() {
    const RP_GC_LIST_TYPESEARCH = ["GrantCriteria", "List", "TypeSearch"];
    return permissions.hasPermission(RP_GC_LIST_TYPESEARCH);
  }

  get hasThanksPointManagementPermission() {
    const RP_TP_LIST_DEFAULT = ["ThanksPoint", "List", "Default"];
    return permissions.hasPermission(RP_TP_LIST_DEFAULT);
  }

  get hasWhatsNewPermission() {
    const RP_WN_LIST = ["WhatsNew", "List"];
    return permissions.hasPermission(RP_WN_LIST);
  }

  get hasCSVUploadPermissions() {
    const RP_CU_USER_REGISTRATION = ["CSVUpload", "Create", "UserRegistration"];
    const RP_CU_USER_GROUP_REGISTRATION = [
      "CSVUpload",
      "Create",
      "UserGroupRegistration"
    ];
    const RP_CU_GROUP_REGISTRATION = [
      "CSVUpload",
      "Create",
      "GroupRegistration"
    ];
    const RP_CU_GRANT_CRITERIA = ["CSVUpload", "Create", "GrantCriteria"];
    const RP_CU_GRANT_POINT = ["CSVUpload", "Create", "GrantPoint"];
    const RP_CU_THANKS_GRANT_POINT = [
      "CSVUpload",
      "Create",
      "ThanksGrantPoint"
    ];
    const RP_CU_GRANT_GRANT_POINT = ["CSVUpload", "Create", "GrantGrantPoint"];
    const RP_CU_RESET_PASSWORD = ["CSVUpload", "Create", "ResetPassword"];
    const RP_CU_THANKS_POINT_APPR_DEN = [
      "CSVUpload",
      "Create",
      "ThanksPointApprovalDenial"
    ];

    const RP_CU_CHANGE_USER_INFORMATION = [
      "CSVUpload",
      "Create",
      "ChangeUserInfo"
    ];
    const RP_CU_USER_GROUPS = ["CSVUpload", "Create", "UserGroups"];
    const RP_CU_USER_INFORMATION = ["CSVUpload", "Create", "UserInfo"];
    const RP_CU_USER_ID = ["CSVUpload", "Create", "UserId"];

    const RP_CU_DELETE_USER_GROUP = ["CSVUpload", "Create", "DeleteUserGroup"];
    const RP_CU_DELETE_USER = ["CSVUpload", "Create", "DeleteUser"];
    const RP_CU_DELETE_POINT = ["CSVUpload", "Create", "DeletePoint"];

    return permissions.hasOnePermissions([
      RP_CU_USER_REGISTRATION,
      RP_CU_USER_GROUP_REGISTRATION,
      RP_CU_GROUP_REGISTRATION,
      RP_CU_GRANT_CRITERIA,
      RP_CU_GRANT_POINT,
      RP_CU_THANKS_GRANT_POINT,
      RP_CU_GRANT_GRANT_POINT,
      RP_CU_RESET_PASSWORD,
      RP_CU_THANKS_POINT_APPR_DEN,
      RP_CU_CHANGE_USER_INFORMATION,
      RP_CU_USER_GROUPS,
      RP_CU_USER_INFORMATION,
      RP_CU_USER_ID,
      RP_CU_DELETE_USER_GROUP,
      RP_CU_DELETE_USER,
      RP_CU_DELETE_POINT
    ]);
  }

  get hasCSVDownloadPermissions() {
    const RP_CD_GROUP = ["CSVDownload", "Read", "Group"];
    const RP_CD_GROUP_POINT = ["CSVDownload", "Read", "GroupPoint"];
    const RP_CD_USER = ["CSVDownload", "Read", "User"];
    const RP_CD_USER_POINT = ["CSVDownload", "Read", "UserPoint"];
    const RP_CD_POINT_EXPIRATION = ["CSVDownload", "Read", "PointExpiration"];
    const RP_CD_GRANT_CRITERIA = ["CSVDownload", "Read", "GrantCriteria"];
    const RP_CD_GRANT_POINT = ["CSVDownload", "Read", "GrantPoint"];
    const RP_CD_THANKS_POINT = ["CSVDownload", "Read", "ThanksPoint"];
    const RP_CD_USE_POINT = ["CSVDownload", "Read", "UsePoint"];
    const RP_CD_PRODUCT_APPLICATION = [
      "CSVDownload",
      "Read",
      "ProductApplication"
    ];
    const RP_CD_UPLOAD_ERROR = ["CSVDownload", "Read", "UploadError"];
    const RP_CD_TAX_DIFFERENCE = ["CSVDownload", "Read", "TaxDifference"];

    return permissions.hasOnePermissions([
      RP_CD_GROUP,
      RP_CD_GROUP_POINT,
      RP_CD_USER,
      RP_CD_USER_POINT,
      RP_CD_POINT_EXPIRATION,
      RP_CD_GRANT_CRITERIA,
      RP_CD_GRANT_POINT,
      RP_CD_THANKS_POINT,
      RP_CD_USE_POINT,
      RP_CD_PRODUCT_APPLICATION,
      RP_CD_UPLOAD_ERROR,
      RP_CD_TAX_DIFFERENCE
    ]);
  }

  get hasSiteCustomizationPermissions() {
    const RP_SC_READ = ["Site", "Read"];
    const RP_SC_EDIT = ["Site", "Update"];

    return permissions.hasOnePermissions([RP_SC_READ, RP_SC_EDIT]);
  }

  get thanksPointName() {
    return getThanksPointName(CONST_DATA.POINT_NAME.THANKS_POINT_DEFAULT_NAME1);
  }

  async created() {
    await permissions.loadPermissions();

    if (this.hasDashboardPermissions) {
      this.naviList.push({
        label: "ダッシュボード",
        url: "/dashboard",
        iconComponentName: "IconBarGraph",
        childList: false,
        dividingLine: false,
        hasNotification: false
      });
    }

    if (
      this.hasGrantPointListPermission ||
      this.hasGrantPointCreatePermissions ||
      this.hasGrantCriteriaListPermission ||
      this.hasThanksPointManagementPermission
    ) {
      const pointList: any = [];

      if (this.hasGrantPointCreatePermissions) {
        pointList.push({
          label: "ポイント付与",
          url: "/grant-point-criteria-select/",
          hasNotification: false
        });
      }

      if (this.hasGrantPointListPermission) {
        pointList.push({
          label: "ポイント付与履歴",
          url: "/grant-point-history-list/",
          hasNotification: false
        });
      }

      if (this.hasGrantCriteriaListPermission) {
        pointList.push({
          label: "ポイント付与基準管理",
          url: "/grant-criteria-list/",
          hasNotification: false
        });
      }

      if (this.hasThanksPointManagementPermission) {
        pointList.push({
          label: this.thanksPointName + "管理",
          url: "/thanks-point-management-list/",
          hasNotification: true
        });
      }

      this.naviList.push({
        label: "ポイント管理",
        url: "",
        iconComponentName: "IconPoint",
        childList: pointList,
        dividingLine: false,
        hasNotification: true
      });
    }

    if (this.hasWhatsNewPermission) {
      this.naviList.push({
        label: "お知らせ管理",
        url: "/whats-new-list/",
        iconComponentName: "IconNote",
        childList: false,
        dividingLine: false,
        hasNotification: false
      });
    }

    if (this.hasCSVUploadPermissions) {
      this.naviList.push({
        label: "CSVアップロード",
        url: "/csv-upload/",
        iconComponentName: "IconUpload",
        childList: false,
        dividingLine: false,
        hasNotification: false
      });
    }

    if (this.hasCSVDownloadPermissions) {
      this.naviList.push({
        label: "CSVダウンロード",
        url: "/csv-download/",
        iconComponentName: "IconDownload",
        childList: false,
        dividingLine: false,
        hasNotification: false
      });
    }

    if (this.hasSiteCustomizationPermissions) {
      this.naviList.push({
        label: "サイトカスタマイズ",
        url: "/site-customization-confirmation/",
        iconComponentName: "IconTools",
        childList: false,
        dividingLine: true,
        hasNotification: false
      });
    }
  }

  initCustomColor() {
    const { colorCode } = this;
    this.hoverColor.true = colorCode;
  }

  onMouseover(state: any) {
    this.currentHoverElementKey = state;
  }

  onClickDropdown(currentKey: any) {
    const { currentOpenDropdown } = this;
    const positionIsExist = currentOpenDropdown.includes(currentKey);
    const processType = positionIsExist ? "remove" : "add";

    const initCurrentOpenDropdown = {
      add() {
        currentOpenDropdown.push(currentKey);
      },

      remove() {
        const position = currentOpenDropdown.indexOf(currentKey);
        currentOpenDropdown.splice(position, 1);
      }
    };

    initCurrentOpenDropdown[processType]();
  }
}
</script>

<style lang="scss" scoped>
.p-the-header-navi-on-admin {
  &__item {
  }

  &__navi {
    &__item {
      &--divid {
        margin-top: rem(18);
        padding-top: rem(18);
        position: relative;

        &::before {
          content: "";
          display: block;
          width: calc(100% - #{rem(24)});
          height: rem(2);
          position: absolute;
          margin: auto;
          top: 0;
          left: 0;
          right: 0;
          background-color: $common-gray-border;
        }
      }
    }
  }
}
</style>
